"use client";

import { useState } from "react";
import Link from "next/link";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import MegaMenuHeader from "@/components/nav/MegaMenuHeader";
import { RATGEBER_PATH, ratgeberTheme, type RatgeberColumn } from "@/lib/ratgeber";
import { cn } from "@/lib/utils";

function Collapse({ open, children }: { open: boolean; children: React.ReactNode }) {
  return (
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.26, ease: [0.16, 1, 0.3, 1] }}
          className="overflow-hidden"
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function MobileCategory({
  column,
  onNavigate,
}: {
  column: RatgeberColumn;
  onNavigate: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-line first:border-t-0">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-start justify-between gap-3 py-3 text-left cursor-pointer"
      >
        <MegaMenuHeader
          icon={column.icon}
          color={column.color}
          title={column.title}
          description={column.description}
        />
        <ChevronDown
          size={16}
          className={cn(
            "shrink-0 mt-3 text-ink-faint transition-transform duration-300",
            open && "rotate-180"
          )}
          aria-hidden="true"
        />
      </button>
      <Collapse open={open}>
        <ul className="flex flex-col pb-3">
          {column.links.map((link) => (
            <li key={link.label} className="min-w-0">
              <Link
                href={link.href}
                onClick={onNavigate}
                className="block truncate rounded-lg px-3 py-2.5 text-sm text-ink-soft hover:bg-paper-deep hover:text-ink transition-colors"
              >
                {link.label}
              </Link>
            </li>
          ))}
          <li>
            <Link
              href={column.footer.href}
              onClick={onNavigate}
              className={cn(
                "inline-flex items-center gap-1.5 px-3 py-2.5 text-sm font-bold",
                ratgeberTheme[column.color].footer
              )}
            >
              {column.footer.label}
              <ArrowRight size={14} aria-hidden="true" />
            </Link>
          </li>
        </ul>
      </Collapse>
    </div>
  );
}

/**
 * „Ratgeber" als Akkordeon im mobilen Menü.
 *
 * `ratgeberColumns` kommt als Prop von der Server-Komponente `Navbar` (siehe
 * dort) statt aus einem direkten Import von `lib/ratgeber-columns` – dieses
 * Modul zieht den kompletten Blog-Datensatz (~60 Artikel) nach, der so nie
 * ins Client-Bundle wandert, nur weil dieses "use client"-Menü ihn bräuchte.
 */
export default function MobileMegaMenu({
  ratgeberColumns,
  onNavigate,
}: {
  ratgeberColumns: RatgeberColumn[];
  onNavigate: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={cn(
          "w-full flex items-center justify-between gap-3 px-3 py-3 rounded-lg font-medium transition-colors cursor-pointer",
          open ? "text-ink font-semibold bg-paper-deep" : "text-ink-soft hover:bg-paper-deep hover:text-ink"
        )}
      >
        <span className="flex items-center gap-3">
          <span
            className={cn("w-1.5 h-1.5 bg-accent shrink-0", open ? "opacity-100" : "opacity-0")}
            aria-hidden="true"
          />
          Ratgeber
        </span>
        <ChevronDown
          size={16}
          className={cn("transition-transform duration-300", open && "rotate-180")}
          aria-hidden="true"
        />
      </button>
      <Collapse open={open}>
        <div className="px-3 pb-2">
          {ratgeberColumns.map((column) => (
            <MobileCategory key={column.id} column={column} onNavigate={onNavigate} />
          ))}
          <div className="border-t border-line">
            <Link
              href={RATGEBER_PATH}
              onClick={onNavigate}
              className="inline-flex items-center gap-1.5 py-3 text-sm font-bold text-accent"
            >
              Alle Ratgeber im Überblick
              <ArrowRight size={14} aria-hidden="true" />
            </Link>
          </div>
        </div>
      </Collapse>
    </div>
  );
}
